import React, { useState } from 'react';
import {
  PageContainer,
  PageHeader,
  PageTag,
  PageTitle,
  PageSubtitle,
  OpenCaptureButton,
  SimulatorLayout,
  PhoneArea,
  PhoneFrame,
  PhoneShell,
  PhoneScreen,
  PhoneNotchIsland,
  PhoneNotchNotch,
  PhoneNotchPunch,
  ControlPanel,
  ControlPanelInner,
  Card,
  CardTitle,
  SectionLabel,
  DeviceGrid,
  DeviceButton,
  DeviceButtonName,
  DeviceButtonSpec,
  ScaleModeRow,
  ScaleModeButton,
  DeviceInfoCard,
  DeviceInfoRow,
  DeviceInfoLabel,
  DeviceInfoValue,
} from './styles';

type NotchType = 'island' | 'notch' | 'punch' | 'none';

type DeviceProfile = {
  id: string;
  name: string;
  os: string;
  resolution: string;
  dpr: number;
  cssWidth: number;
  cssHeight: number;
  screenRadius: number;
  notch: NotchType;
};

const DEVICES: DeviceProfile[] = [
  {
    id: 'iphone-16-pro',
    name: 'iPhone 16 Pro',
    os: 'iOS 18',
    resolution: '1206×2622',
    dpr: 3,
    cssWidth: 402,
    cssHeight: 874,
    screenRadius: 44,
    notch: 'island',
  },
  {
    id: 'iphone-14',
    name: 'iPhone 14',
    os: 'iOS 17',
    resolution: '1170×2532',
    dpr: 3,
    cssWidth: 390,
    cssHeight: 844,
    screenRadius: 40,
    notch: 'island',
  },
  {
    id: 'iphone-se-3',
    name: 'iPhone SE 3',
    os: 'iOS 17',
    resolution: '750×1334',
    dpr: 2,
    cssWidth: 375,
    cssHeight: 667,
    screenRadius: 14,
    notch: 'none',
  },
  {
    id: 'pixel-8',
    name: 'Pixel 8',
    os: 'Android 14',
    resolution: '1080×2400',
    dpr: 2.625,
    cssWidth: 412,
    cssHeight: 915,
    screenRadius: 30,
    notch: 'punch',
  },
  {
    id: 'pixel-7a',
    name: 'Pixel 7a',
    os: 'Android 14',
    resolution: '1080×2400',
    dpr: 2.625,
    cssWidth: 412,
    cssHeight: 915,
    screenRadius: 28,
    notch: 'punch',
  },
  {
    id: 'samsung-s24',
    name: 'Samsung S24',
    os: 'Android 14',
    resolution: '1080×2340',
    dpr: 3,
    cssWidth: 360,
    cssHeight: 780,
    screenRadius: 30,
    notch: 'punch',
  },
  {
    id: 'samsung-s24u',
    name: 'Samsung S24 Ultra',
    os: 'Android 14',
    resolution: '1440×3088',
    dpr: 4,
    cssWidth: 412,
    cssHeight: 915,
    screenRadius: 36,
    notch: 'punch',
  },
  {
    id: 'moto-g84',
    name: 'Moto G84',
    os: 'Android 13',
    resolution: '1080×2400',
    dpr: 2.5,
    cssWidth: 390,
    cssHeight: 844,
    screenRadius: 24,
    notch: 'punch',
  },
  {
    id: 'redmi-note-13',
    name: 'Redmi Note 13',
    os: 'Android 13',
    resolution: '1080×2400',
    dpr: 2.75,
    cssWidth: 392,
    cssHeight: 873,
    screenRadius: 26,
    notch: 'punch',
  },
  {
    id: 'poco-x6',
    name: 'POCO X6',
    os: 'Android 14',
    resolution: '1220×2712',
    dpr: 2.625,
    cssWidth: 412,
    cssHeight: 915,
    screenRadius: 28,
    notch: 'punch',
  },
  {
    id: 'nokia-g42',
    name: 'Nokia G42',
    os: 'Android 13',
    resolution: '720×1600',
    dpr: 2,
    cssWidth: 360,
    cssHeight: 800,
    screenRadius: 20,
    notch: 'punch',
  },
  {
    id: 'iphone-12-mini',
    name: 'iPhone 12 Mini',
    os: 'iOS 16',
    resolution: '1080×2340',
    dpr: 3,
    cssWidth: 360,
    cssHeight: 780,
    screenRadius: 38,
    notch: 'notch',
  },
  {
    id: 'galaxy-a54',
    name: 'Galaxy A54',
    os: 'Android 14',
    resolution: '1080×2340',
    dpr: 3,
    cssWidth: 360,
    cssHeight: 780,
    screenRadius: 26,
    notch: 'punch',
  },
  {
    id: 'galaxy-a15',
    name: 'Galaxy A15',
    os: 'Android 14',
    resolution: '1080×2340',
    dpr: 3,
    cssWidth: 360,
    cssHeight: 780,
    screenRadius: 22,
    notch: 'notch',
  },
  {
    id: 'oppo-a78',
    name: 'Oppo A78',
    os: 'Android 13',
    resolution: '1080×2400',
    dpr: 2.5,
    cssWidth: 390,
    cssHeight: 844,
    screenRadius: 24,
    notch: 'punch',
  },
  {
    id: 'asus-rog8',
    name: 'ASUS ROG 8',
    os: 'Android 14',
    resolution: '1080×2400',
    dpr: 2.625,
    cssWidth: 412,
    cssHeight: 915,
    screenRadius: 26,
    notch: 'none',
  },
];

const SCALE_OPTIONS = [
  { label: 'Fit (75%)', value: 0.75 },
  { label: '50%', value: 0.5 },
  { label: '35%', value: 0.35 },
  { label: '25%', value: 0.25 },
];

const NotchOverlay: React.FC<{ type: NotchType }> = ({ type }) => {
  if (type === 'island') return <PhoneNotchIsland />;
  if (type === 'notch') return <PhoneNotchNotch />;
  if (type === 'punch') return <PhoneNotchPunch />;
  return null;
};

export const TestUnicoPage: React.FC = () => {

  const [selectedDevice, setSelectedDevice] = useState<DeviceProfile>(DEVICES[0]);
  const [scaleIndex, setScaleIndex] = useState(0);
  const scale = SCALE_OPTIONS[scaleIndex].value;

  const scaledW = selectedDevice.cssWidth * scale;
  const scaledH = selectedDevice.cssHeight * scale;

  const captureEmbedUrl = `/test-unico/capture?embed=1&device=${selectedDevice.id}`;

  return (
    <PageContainer>
      <PageHeader>
        <PageTag>Dev Tools · INSS Biometria</PageTag>
        <PageTitle>Teste Unico — Liveness</PageTitle>
        <PageSubtitle>
          Simule a captura biométrica em um dispositivo móvel virtual. Selecione o perfil, ajuste a escala e inicie
          dentro do emulador.
        </PageSubtitle>
      </PageHeader>

      <SimulatorLayout>
        {/* ── Coluna esquerda: celular ── */}
        <PhoneArea>
          <ScaleModeRow>
            {SCALE_OPTIONS.map((opt, i) => (
              <ScaleModeButton key={opt.label} $active={i === scaleIndex} onClick={() => setScaleIndex(i)}>
                {opt.label}
              </ScaleModeButton>
            ))}
          </ScaleModeRow>

          <PhoneFrame>
            <PhoneShell $width={scaledW} $radius={selectedDevice.screenRadius}>
              <PhoneScreen $width={scaledW} $height={scaledH} $radius={selectedDevice.screenRadius}>
                <NotchOverlay type={selectedDevice.notch} />
                <iframe
                  key={captureEmbedUrl}
                  src={captureEmbedUrl}
                  allow="camera; microphone"
                  style={{
                    width: selectedDevice.cssWidth,
                    height: selectedDevice.cssHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    border: 'none',
                    display: 'block',
                  }}
                />
              </PhoneScreen>
            </PhoneShell>
          </PhoneFrame>
        </PhoneArea>

        {/* ── Coluna direita: controles + info ── */}
        <ControlPanel>
          <ControlPanelInner>
            <OpenCaptureButton href={`/test-unico/capture?device=${selectedDevice.id}`} target="_blank" rel="noopener noreferrer">
              ↗ Abrir captura em nova aba
            </OpenCaptureButton>

            {/* Specs do dispositivo selecionado */}
            <DeviceInfoCard>
              <CardTitle style={{ marginBottom: 12 }}>{selectedDevice.name}</CardTitle>
              {[
                ['Sistema', selectedDevice.os],
                ['Resolução', selectedDevice.resolution],
                ['DPR', String(selectedDevice.dpr)],
                ['Viewport CSS', `${selectedDevice.cssWidth}×${selectedDevice.cssHeight}px`],
                ['Escala atual', `${Math.round(scale * 100)}%`],
                ['Notch', selectedDevice.notch],
              ].map(([label, value]) => (
                <DeviceInfoRow key={label}>
                  <DeviceInfoLabel>{label}</DeviceInfoLabel>
                  <DeviceInfoValue>{value}</DeviceInfoValue>
                </DeviceInfoRow>
              ))}
            </DeviceInfoCard>

            {/* Seletor de dispositivo */}
            <Card>
              <SectionLabel>Dispositivo</SectionLabel>
              <DeviceGrid>
                {DEVICES.map((d) => (
                  <DeviceButton key={d.id} $active={d.id === selectedDevice.id} onClick={() => setSelectedDevice(d)}>
                    <DeviceButtonName>{d.name}</DeviceButtonName>
                    <DeviceButtonSpec>{d.os}</DeviceButtonSpec>
                  </DeviceButton>
                ))}
              </DeviceGrid>
            </Card>
          </ControlPanelInner>
        </ControlPanel>
      </SimulatorLayout>

      {/* ── Linha inferior: Como usar + Flows relevantes ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle>Como usar</CardTitle>
          <ol style={{ fontSize: 13, color: '#475569', paddingLeft: 18, margin: 0, lineHeight: 1.7 }}>
            <li>Selecione um dispositivo na grade acima.</li>
            <li>Ajuste a escala para visualização.</li>
            <li>
              Clique em <strong>Iniciar Captura</strong> dentro do emulador.
            </li>
            <li>Conceda permissão de câmera quando solicitado.</li>
            <li>
              O <strong>process_id</strong> é emitido no topo ao iniciar.
            </li>
            <li>Após a captura, os dados são exibidos na tela de resultado.</li>
          </ol>
        </Card>

        <Card>
          <CardTitle>Flows relevantes</CardTitle>
          <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.7 }}>
            Ativado quando <code>IsLivenessSignature = true</code> nos convênios{' '}
            <strong>Aposentado/Pensionista INSS</strong>, <strong>Cartão Benefício RCC</strong> e <strong>RMC</strong>{' '}
            (IDs 18, 19, 20).
          </p>
        </Card>
      </div>
    </PageContainer>
  );
};
